import { useEffect } from "react";

export interface ShortcutSpec {
  /** True when matched. */
  match: (e: KeyboardEvent) => boolean;
  handler: (e: KeyboardEvent) => void;
  description: string;
}

const isEditable = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
};

export function useShortcuts(specs: ShortcutSpec[], enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      // Ignore plain-letter shortcuts when the user is typing into a field.
      // Modifier shortcuts (Ctrl+K) still fire, since the user means them.
      const editable = isEditable(e.target);
      for (const spec of specs) {
        if (spec.match(e)) {
          if (editable && !e.metaKey && !e.ctrlKey && !e.altKey) continue;
          spec.handler(e);
          break;
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [specs, enabled]);
}

// Common matchers
export const matchKey = (key: string) => (e: KeyboardEvent) =>
  e.key.toLowerCase() === key.toLowerCase() && !e.ctrlKey && !e.metaKey && !e.altKey;

export const matchCtrlKey = (key: string) => (e: KeyboardEvent) =>
  (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === key.toLowerCase();
